import { useState, useEffect } from 'react'
import { format } from 'date-fns'
import type { TattooTimelineEntry } from '@/types/models'
import { ChevronLeft, ChevronRight, X, Check } from 'lucide-react'

interface TattooDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tattoo: TattooTimelineEntry | null
  onAddHealed?: () => void
}

export function TattooDetailDialog({
  open,
  onOpenChange,
  tattoo,
  onAddHealed
}: TattooDetailDialogProps) {
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0)
  const [viewingHealed, setViewingHealed] = useState(false)
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!tattoo || !mounted || !open) return null

  const currentPhotos = viewingHealed ? tattoo.healedPhotos : tattoo.freshPhotos
  const hasHealed = tattoo.healedPhotos.length > 0
  const currentPhoto = currentPhotos[currentPhotoIndex]

  const nextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % currentPhotos.length)
  }

  const prevPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + currentPhotos.length) % currentPhotos.length)
  }

  const handleClose = () => {
    setCurrentPhotoIndex(0)
    setViewingHealed(false)
    onOpenChange(false)
  }

  const toggleView = () => {
    setViewingHealed(!viewingHealed)
    setCurrentPhotoIndex(0)
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-50 bg-black/50 animate-in fade-in-0"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center pointer-events-none p-4">
        <div className="bg-background rounded-[var(--card-radius)] shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 pointer-events-auto">
          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-1 text-foreground hover:opacity-70 transition-opacity focus:outline-none z-10"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Toggle view button */}
          {hasHealed && (
            <div className="p-6 pb-4 flex justify-end">
              <button
                onClick={toggleView}
                className="px-3 py-1 rounded-lg bg-muted hover:bg-muted/80 text-foreground text-sm font-medium transition-colors"
              >
                {viewingHealed ? 'View Fresh' : 'View Healed'}
              </button>
            </div>
          )}

          {/* Content */}
          <div className={`space-y-4 ${hasHealed ? 'p-6 pt-0' : 'p-6'}`}>
            {/* Photo viewer */}
            <div className="flex items-center justify-center">
              {currentPhoto && (
                <div className="relative inline-block">
                  <img
                    src={currentPhoto.url}
                    alt={tattoo.description || 'Tattoo'}
                    className="h-[300px] w-auto object-contain rounded-lg"
                  />

                  {/* Status badge - Top Right */}
                  <div className={`absolute top-2 right-2 text-white text-xs px-2 py-1 rounded-full font-medium flex items-center gap-1 ${
                    viewingHealed ? 'bg-green-500' : 'bg-primary'
                  }`}>
                    {viewingHealed ? (
                      <>
                        <Check className="w-3 h-3" />
                        Healed
                      </>
                    ) : (
                      'Fresh'
                    )}
                  </div>

                  {/* Date badge - Middle Bottom */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-background text-foreground text-xs px-2 py-1 rounded-full">
                    {format(tattoo.createdAt, 'd.MM.yyyy')}
                  </div>
                </div>
              )}
            </div>

            {/* Navigation buttons */}
            {currentPhotos.length > 1 && (
              <div className="flex items-center justify-center gap-3">
                <button
                  onClick={prevPhoto}
                  className="p-1.5 rounded-full bg-background hover:bg-background/80 flex items-center justify-center transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-foreground" />
                </button>
                <span className="text-sm text-muted-foreground">
                  {currentPhotoIndex + 1} / {currentPhotos.length}
                </span>
                <button
                  onClick={nextPhoto}
                  className="p-1.5 rounded-full bg-background hover:bg-background/80 flex items-center justify-center transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-foreground" />
                </button>
              </div>
            )}

            {/* Tattoo details */}
            <div className="space-y-3">
              {tattoo.description && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Description</h4>
                  <p className="text-sm text-muted-foreground">{tattoo.description}</p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                {tattoo.bodyPlacement && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">Placement</h4>
                    <p className="text-sm text-muted-foreground capitalize">{tattoo.bodyPlacement}</p>
                  </div>
                )}

                {tattoo.size && (
                  <div>
                    <h4 className="text-sm font-semibold text-foreground mb-1">Size</h4>
                    <p className="text-sm text-muted-foreground">{tattoo.size}</p>
                  </div>
                )}
              </div>

              {tattoo.style && (
                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-1">Style</h4>
                  <p className="text-sm text-muted-foreground capitalize">{tattoo.style}</p>
                </div>
              )}
            </div>

            {/* Add healed photos button */}
            {!hasHealed && onAddHealed && (
              <button
                onClick={() => {
                  handleClose()
                  onAddHealed()
                }}
                className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 rounded-lg transition-colors"
              >
                Upload Healed
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

